/**
 * The in-page confirmation every consequential act uses in place of a native `window.confirm()`
 * (PB-05): a title, what it will do, an optional typed reason, and a confirm/cancel pair, shown in
 * the app's `GlobalModal`.
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/modal/add-team-member.tsx
 *   the modal view body: title, close action, fields, footer buttons.
 *
 * `reason` set → a required textarea, and the trimmed text is handed to `onConfirm` (the act's
 * backend records it). Unset → a plain confirmation, for acts whose backend takes no reason.
 * `onConfirm` unset → a notice: it only says why the act cannot happen yet, with a Close button.
 */
import { useState, type ReactNode } from "react";
import { Button, Textarea, Title } from "rizzui";
import { useModal } from "@steelyard/theme";

export type ConfirmOptions = {
  title: string;
  /** What this does and what it cannot undo. */
  consequence: ReactNode;
  confirmLabel?: string;
  /** Label for a required reason field; omit for acts that take none. */
  reason?: string;
  onConfirm?: (reason: string) => void;
};

export function ConfirmBody({ title, consequence, confirmLabel, reason, onConfirm, onCancel }: ConfirmOptions & { onCancel: () => void }) {
  const [typed, setTyped] = useState("");
  const trimmed = typed.trim();

  return (
    <form
      className="flex flex-col gap-4 p-6"
      onSubmit={(event) => {
        event.preventDefault();
        if (!onConfirm || (reason && !trimmed)) return;
        onConfirm(trimmed);
      }}
    >
      <Title as="h4">{title}</Title>
      <div>{consequence}</div>
      {reason ? (
        <Textarea label={reason} value={typed} onChange={(event) => setTyped(event.target.value)} rows={3} required />
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {onConfirm ? "Cancel" : "Close"}
        </Button>
        {onConfirm ? (
          <Button type="submit" color="danger" disabled={Boolean(reason) && !trimmed}>
            {confirmLabel}
          </Button>
        ) : null}
      </div>
    </form>
  );
}

/** `confirm({...})` opens the modal; the modal closes as soon as the act is confirmed or cancelled. */
export function useConfirm() {
  const { openModal, closeModal } = useModal();

  return (options: ConfirmOptions) =>
    openModal({
      size: "md",
      view: (
        <ConfirmBody
          {...options}
          onCancel={closeModal}
          onConfirm={
            options.onConfirm &&
            ((reason) => {
              closeModal();
              options.onConfirm?.(reason);
            })
          }
        />
      ),
    });
}
